import {Box, Typography} from '@mui/material';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {t} from 'i18next';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';
import './ExportFormat.scss';
import {useDispatch, useSelector} from 'react-redux';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../shared/types';
import {useEffect, useState} from 'react';
import {useFileSettings} from '../../../hooks/documentPrinting/useDocumentPrinting';
import {
  FieldItem,
  IFileSettingsResponse,
} from '../../../api/types/documentPrinting.type';
import ArrRadio from '../../common/arr-radio/ArrRadio';
import {setSaveBatchPrint} from '../../../redux/slice/batchPrintSlice';

const shortInfo = () => (
  <Box component="div" role="tooltip">
    <Typography component="p" tabIndex={0} data-testid={`export-format-title`}>
      {t('batchPrint.exportFormatGroup.tooltip.title')}
    </Typography>

    <ul
      data-testid={`export-format-step1title-step1description-step2title-step2description-step3title-step3description`}
    >
      <li data-testid={`export-format-step1title-step1description`}>
        <span
          className="tooltip-list-title"
          data-testid={`export-format-step1title`}
        >
          {t('batchPrint.exportFormatGroup.tooltip.step1Title')}
        </span>
        {t('batchPrint.exportFormatGroup.tooltip.step1Description')}
      </li>
      <li data-testid={`export-format-step2title-step2description`}>
        <span
          className="tooltip-list-title"
          data-testid={`export-format-step2title`}
        >
          {t('batchPrint.exportFormatGroup.tooltip.step2Title')}
        </span>
        {t('batchPrint.exportFormatGroup.tooltip.step2Description')}
      </li>
      <li data-testid={`export-format-step3title-step3description`}>
        <span
          className="tooltip-list-title"
          data-testid={`export-format-step3title`}
        >
          {t('batchPrint.exportFormatGroup.tooltip.step3Title')}
        </span>
        {t('batchPrint.exportFormatGroup.tooltip.step3Description')}
      </li>
    </ul>
    <p data-testid={`export-format-step4description`}>
      {t('batchPrint.exportFormatGroup.tooltip.step4Description')}
    </p>
  </Box>
);

const sortFieldOrderList = [
  {
    id: 'ASC',
    name: 'ASC',
  },
  {
    id: 'DESC',
    name: 'DESC',
  },
];

const ExportFormat = () => {
  const exportFormatHeadingId = 'export-format-heading';
  const pdfCreationLabelId = 'export-pdf-creation-label';
  const exportFileLabelId = 'export-file-name-label';
  const sortByLabelId = 'export-sort-by-label';

  const dispatch = useDispatch();
  const saveBatchPrint = useSelector(
    (state: any) => state.batchPrint.saveBatchPrint.batchPrintDTO
  );
  const [fileName, setFileName] = useState<ISelectItem | null>(null);
  const [sortByField, setSortByField] = useState<ISelectItem | null>(null);
  const [fileSettingsData, setFileSettingsData] =
    useState<IFileSettingsResponse>({});
  const [sortByFieldOrder, setSortByFieldOrder] = useState<ISelectItem | null>(
    null
  );
  const native = saveBatchPrint?.printIssueSettingCode === 'SKIPPRINTBUTFOLDER';
  const [pdfOption, setPdfOption] = useState<string | null>(
    saveBatchPrint?.shouldCreateAllInOnePDF ? '2' : '1'
  );

  const {data: getFileSettingsData} = useFileSettings(
    saveBatchPrint?.printIssueSettingCode === 'SKIPPRINTBUTFOLDER'
      ? false
      : true
  );

  useEffect(() => {
    if (!getFileSettingsData) return;

    const {
      nativeFileNameList = [],
      nativeSortyByList = [],
      productionNameList = [],
      productionSortyByList = [],
    } = getFileSettingsData;

    const pickField = (
      savedFieldID: number,
      savedFieldName: string,
      nativeList: FieldItem[],
      productionList: FieldItem[]
    ) => {
      const list = native ? nativeList : productionList;
      return {
        id:
          savedFieldID && savedFieldID !== 0 ? savedFieldID : list[0]?.fieldID,
        name: savedFieldName ? savedFieldName : list[0]?.fieldLabel,
      };
    };

    setFileSettingsData(getFileSettingsData);

    const newFileName = pickField(
      saveBatchPrint?.exportFileNameFieldID,
      saveBatchPrint?.exportFileNameFieldName,
      nativeFileNameList,
      productionNameList
    );

    const newSortByField = pickField(
      saveBatchPrint?.sortByFieldID,
      saveBatchPrint?.sortByFieldName,
      nativeSortyByList,
      productionSortyByList
    );

    const newSortByFieldOrder = {
      id:
        saveBatchPrint?.sortOrder && saveBatchPrint.sortOrder !== ''
          ? saveBatchPrint.sortOrder
          : sortFieldOrderList[0]?.id,
      name:
        saveBatchPrint?.sortOrder && saveBatchPrint.sortOrder !== ''
          ? saveBatchPrint.sortOrder
          : sortFieldOrderList[0]?.name,
    };

    setFileName(newFileName);
    setSortByField(newSortByField);
    setSortByFieldOrder(newSortByFieldOrder);

    dispatch(
      setSaveBatchPrint({
        exportFileNameFieldID: newFileName.id,
        exportFileNameFieldName: newFileName.name,
        sortByFieldID: newSortByField.id,
        sortByFieldName: newSortByField.name,
        sortOrder: newSortByFieldOrder.name,
      })
    );
  }, [getFileSettingsData]);

  const handleFileName = (fileName: ISelectItem | null) => {
    dispatch(setSaveBatchPrint({exportFileNameFieldID: fileName?.id}));
    dispatch(setSaveBatchPrint({exportFileNameFieldName: fileName?.name}));
  };

  const handleSortByField = (fileName: ISelectItem | null) => {
    dispatch(setSaveBatchPrint({sortByFieldID: fileName?.id}));
    dispatch(setSaveBatchPrint({sortByFieldName: fileName?.name}));
  };

  const handleSortOrder = (fileName: ISelectItem | null) => {
    dispatch(setSaveBatchPrint({sortOrder: fileName?.name}));
  };

  const handlePdfOption = (value: string) => {
    setPdfOption(value);
    if (value === '1') {
      dispatch(setSaveBatchPrint({shouldCreateAllInOnePDF: false}));
    } else {
      dispatch(setSaveBatchPrint({shouldCreateAllInOnePDF: true}));
    }
  };

  return (
    <Box
      className="export-format-container"
      component="section"
      role="region"
      aria-labelledby={exportFormatHeadingId}
      data-testid="export-format-container"
    >
      <Box className="export-format-header " data-testid="export-format-header">
        <Typography
          data-testid="export-format-title-text"
          id={exportFormatHeadingId}
          component="h2"
          className="typo-text subhead2_size"
          tabIndex={0}
        >
          {t('batchPrint.exportFormatGroup.title')}
        </Typography>
        <ArrTooltipWrapper
          title={t('batchPrint.exportFormatGroup.title')}
          trigger={
            <button
              type="button"
              aria-label={t('common.infoTooltip')}
              data-testid="add-user-title-info"
              className="tooltip-trigger"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.currentTarget.click();
                }
              }}
              style={{
                background: 'transparent',
                border: 0,
                padding: 0,
                margin: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <InfoIcon
                fontSize="small"
                className="info-icon"
                aria-hidden="true"
                focusable="false"
              />
            </button>
          }
          closeTooltipOnEscPress={true}
        >
          {shortInfo()}
        </ArrTooltipWrapper>
      </Box>
      <Box
        className="source-selection-body"
        data-testid="export-format-source-selection-body"
      >
        <Box
          className="dropdown-container"
          component="section"
          data-testid="export-format-file-section"
        >
          <Typography
            data-testid={`export-format-export-file`}
            component="h3"
            className="typo-text subhead4_size file-label"
            id={exportFileLabelId}
            tabIndex={0}
          >
            {t('batchPrint.exportFormatGroup.exportFile')}
          </Typography>
          <Box
            className="dropdown-field"
            data-testid="export-format-file-dropdown-field"
          >
            <ArrSingleSelectDropdown
              dropdownLabel={t('common.select')}
              dropdownItems={
                native
                  ? (fileSettingsData?.nativeFileNameList?.map((item) => ({
                      id: item.fieldID ?? 0,
                      name: item.fieldLabel,
                    })) ?? [])
                  : (fileSettingsData?.productionNameList?.map((item) => ({
                      id: item.fieldID ?? 0,
                      name: item.fieldLabel,
                    })) ?? [])
              }
              tabIndex={-1}
              selectedValues={fileName}
              onSelectionChange={handleFileName}
              sizeVariant="medium"
              dataTestId="asd-export-format-file"
              selectRestProps={{
                'aria-label': t('batchPrint.exportFormatGroup.exportFile'),
              }}
            />
          </Box>
        </Box>
        <Box
          className="dropdown-container"
          component="section"
          data-testid="export-format-sort-section"
        >
          <Typography
            data-testid={`export-format-sort-by`}
            component="h3"
            className="typo-text subhead4_size file-label"
            id={sortByLabelId}
            tabIndex={0}
          >
            {t('batchPrint.exportFormatGroup.sortBy')}
          </Typography>
          <Box
            className="dropdown-field"
            data-testid="export-format-sort-by-dropdown-field"
          >
            <ArrSingleSelectDropdown
              dropdownLabel={t('common.select')}
              dropdownItems={
                native
                  ? (fileSettingsData?.nativeSortyByList?.map((item) => ({
                      id: item.fieldID ?? 0,
                      name: item.fieldLabel,
                    })) ?? [])
                  : (fileSettingsData?.productionSortyByList?.map((item) => ({
                      id: item.fieldID ?? 0,
                      name: item.fieldLabel,
                    })) ?? [])
              }
              selectedValues={sortByField}
              onSelectionChange={handleSortByField}
              sizeVariant="medium"
              searchable
              showNoResultsText
              dataTestId="asd-export-format-sortbyfield"
              selectRestProps={{
                'aria-label': t('batchPrint.exportFormatGroup.sortBy'),
              }}
              tabIndex={-1}
            />
          </Box>
          <Box
            className="dropdown-field"
            data-testid="export-format-sort-order-dropdown-field"
          >
            <ArrSingleSelectDropdown
              dropdownLabel={t('common.select')}
              dropdownItems={sortFieldOrderList}
              selectedValues={sortByFieldOrder}
              onSelectionChange={handleSortOrder}
              sizeVariant="medium"
              dataTestId="asd-export-format-sortbyfieldOrder"
              selectRestProps={{
                'aria-label': 'Sort order',
              }}
              tabIndex={-1}
            />
          </Box>
        </Box>
        <Box
          className="dropdown-container"
          component="section"
          data-testid="export-format-pdf-creation-section"
        >
          <Typography
            data-testid={`export-format-pdf-creation`}
            id={pdfCreationLabelId}
            component="h3"
            className="typo-text subhead4_size file-label"
            tabIndex={0}
          >
            {t('batchPrint.exportFormatGroup.pdfCreation')}
          </Typography>
          <Box
            data-testid="export-format-radio-field"
            className="radio-field"
            role="radiogroup"
            aria-labelledby={pdfCreationLabelId}
          >
            <ArrRadio
              dataTestId={`pdf-option-radio-button-${pdfOption}`}
              name="pdf-option"
              tabIndex={0}
              value={pdfOption?.toString() ?? ''}
              onChange={(val: string) => handlePdfOption(val)}
              data={[
                {
                  value: '1',
                  label: 'One PDF for Each document',
                },
                {
                  value: '2',
                  label: 'One PDF for all documents',
                },
              ]}
              size="xs"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ExportFormat;
